import PsLayout from "pages/structured/layout/PsLayout"
import "../builder.css"
import "./recap.css"
import { useEffect, useState, useContext } from "react"
import AuthContext from "contexte/AuthContext"
import { useTranslation } from 'react-i18next';
import PageLoader from "components/pageLoader/PageLoader";
import { useNavigate } from "react-router-dom"
import { validateSpDraft, fetchSpDraft } from "services/spDraftServices"
import Notification from "components/notifications/Notification";
import { launchError, launchSuccess } from "components/notifications";
import { removeExchangeFromTicker } from "utils/display";

const Recap = () => {

  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState();
  const [product, setProduct] = useState({});
  const [lang, setLang] = useState('fr');

  const fieldKey = (base) => lang === 'en' ? `${base}_en` : base;


  const fetchActualData = async () => {
    try {
      const response = await fetchSpDraft(user, window.location.pathname.split('/')[2]);
      setProduct(response)
      setLoading(false)
    } catch (e) {
      console.log(e);
      setLoading(false)
      setError(e)
    }
  }

  useEffect(() => {
    fetchActualData();
  }, [])

  const getYieldFrequencyText = (frequency) => {
    switch (frequency) {
      case "monthly":
        return "Mensuel";
      case "annualy":
        return "Annuel";
      case "quarterly":
        return "Trimestriel";
      default:
        return "";
    }
  };

  const VALIDATION_ERRORS = {
    already_validated: 'Ce brouillon a déjà été validé',
    missing_collecting_date: 'Date de collecte manquante',
    missing_minimum_invest: 'Minimums avant/après ouverture manquants (onglet Financement)',
    collecting_date_in_past: 'La date de collecte est passée',
  }

  const launchSp = async (e, force = false) => {
    e?.preventDefault();
    try {
      await validateSpDraft(user, product._id, force);
      setProduct({ ...product, validated_at: new Date().toISOString() })
      launchSuccess(t('notifications.structured_products.validate.success'))
    } catch (err) {
      const data = err?.response?.data;
      if (data?.error === 'launch_schedule_busy') {
        const date = new Date(data.date).toLocaleString('fr-FR', { timeZone: 'Europe/Paris' });
        if (window.confirm(`Le lancement du produit ${data.product_id} est déjà programmé le ${date}. Le remplacer par ce produit ?`))
          return launchSp(null, true);
        return;
      }
      if (data?.error === 'missing_fields')
        return launchError(`Champs manquants : ${data.fields.join(', ')}`);
      launchError(VALIDATION_ERRORS[data?.error] || t('notifications.structured_products.validate.error'))
    }
  }

  const formatParisDate = (date) => date ? new Date(date).toLocaleString('fr-FR', { timeZone: 'Europe/Paris', dateStyle: 'long', timeStyle: 'short' }) : '-';


  return (
    <PsLayout>
      <Notification/>
      {
        loading ? (
          <div className='pageLoaderBox'>
            <PageLoader />
          </div>
        ) : (
          error ? (
            <div>Erreur</div>
          ) : (
            <form className="recap-page" onSubmit={launchSp}>
              <div className="recap-first">
                <div className="recap-first-left" style={{
                  backgroundImage: "url(" + product?.picture + ")",
                  backgroundPosition: 'center',
                  backgroundSize: 'cover',
                  backgroundRepeat: 'no-repeat'
                }}>
                  <div className="recap-first-left-absolute">
                    <p className="no-padding">{product?.name}</p>
                    <p className="no-padding">Par {product?.provider}</p>
                  </div>
                </div>
                <div className="recap-first-right">
                  <span className="recap-small-text">ISIN {product?.isin}</span>
                  <p className="no-padding product-title">{product?.name} - {product?.total}€</p>
                  <div className="recap-first-grid">
                    <div className="recap-first-grid-item">
                      <p className="no-padding recap-first-grid-title">Rendements</p>
                      <p className="no-padding recap-first-grid-data">{product?.yield}%</p>
                    </div>
                    <div className="recap-first-grid-item">
                      <p className="no-padding recap-first-grid-title">Versement</p>
                      <p className="no-padding recap-first-grid-data">{getYieldFrequencyText(product?.yield_frequency)}</p>
                    </div>
                    <div className="recap-first-grid-item">
                      <p className="no-padding recap-first-grid-title">Barrière</p>
                      <p className="no-padding recap-first-grid-data">{product?.barrier_capital}%</p>
                    </div>
                    <div className="recap-first-grid-item">
                      <p className="no-padding recap-first-grid-title">Durée</p>
                      <p className="no-padding recap-first-grid-data">{product?.maturity} mois</p>
                    </div>
                    <div className="recap-first-grid-item">
                      <p className="no-padding recap-first-grid-title">Min. avant ouverture</p>
                      <p className="no-padding recap-first-grid-data">{product?.minimum_invest ?? '-'}€</p>
                    </div>
                    <div className="recap-first-grid-item">
                      <p className="no-padding recap-first-grid-title">Min. après ouverture</p>
                      <p className="no-padding recap-first-grid-data">{product?.circle_minimum_invest ?? '-'}€</p>
                    </div>
                    <div className="recap-first-grid-item">
                      <p className="no-padding recap-first-grid-title">Utilisateurs additionnels</p>
                      <p className="no-padding recap-first-grid-data">{product?.additional_users ?? 0}</p>
                    </div>
                    <div className="recap-first-grid-item">
                      <p className="no-padding recap-first-grid-title">Collecte</p>
                      <p className="no-padding recap-first-grid-data">{formatParisDate(product?.collecting_date)}</p>
                    </div>
                  </div>
                </div>
              </div>
              <hr />
              <div className="recap-first">
                <div className="recap-indices">
                  <p className="no-padding">Indices de référence</p>
                  {
                    product.products?.map((ticker, index) => (
                      <div key={index} className="psform-ticker-selected-box pointer">
                        <div className="ticker-desc">
                          <img className="ticker-img" src={`https://cdn.goliaths.io/stocks/${removeExchangeFromTicker(ticker.ticker)}.png`} alt="" />
                          <span className="psform-input-label">{ticker.name}</span>
                        </div>
                      </div >
                    ))
                  }
                </div>
                <div className="recap-first-right">
                  <div className="recap-user-box">
                    <img className="recap-user-pic" src={`https://cdn.goliaths.io/usr/pic/${product?.user?.uuid}`} alt="" />
                    <span className="recap-user-name">{product?.user?.name}</span>
                  </div>

                </div>
              </div>
              <hr />
              <div className="psform-lang-tabs">
                <div className={lang === 'fr' ? "psform-lang-tab-selected" : "psform-lang-tab"} onClick={() => setLang('fr')}>FR</div>
                <div className={lang === 'en' ? "psform-lang-tab-selected" : "psform-lang-tab"} onClick={() => setLang('en')}>EN</div>
              </div>
              <div className="recap-text-box">
                <p className="no-padding">{product?.[fieldKey('sub_description')]}</p>
                <div className="recap-desc-box">
                  {
                    product?.[fieldKey('descriptions')]?.map((item, index) => (
                      <div className="recap-desc-item" key={index}>
                        <p className="no-padding recap-desc-title">{item.title}</p>
                        <p className="no-padding recap-desc-message">{item.message}</p>
                      </div>
                    )
                    )
                  }
                </div>
              </div>
              <div className="recap-text-box">
                <p className="no-padding recap-desc-title additional-infos">Infos supplémentaires</p>
                <div className="recap-desc-box">
                  {
                    product?.[fieldKey('more_info')]?.map((item, index) => (
                      <div className="recap-desc-item" key={index}>
                        <p className="no-padding recap-desc-title">{item.title}</p>
                        <p className="no-padding recap-desc-message">{item.message}</p>
                      </div>
                    )
                    )
                  }
                </div>
              </div>
              <div className="recap-text-box">
                <p className="no-padding recap-desc-title additional-infos">Documents</p>
                <div className="recap-desc-box">
                  {
                    product?.[fieldKey('documents')]?.map((item, index) => (
                      <div className="recap-desc-item" key={index}>
                        <p className="no-padding recap-desc-title">
                          <a href={item.url} target="_blank" rel="noreferrer">{item.title}</a>
                        </p>
                      </div>
                    )
                    )
                  }
                </div>
              </div>
              <div className="psform-submit-box">
                {
                  product?.validated_at ? (
                    <p className="no-padding">Validé le {formatParisDate(product.validated_at)}</p>
                  ) : (
                    <button type="submit" className="psform-submit">Valider et publier le produit</button>
                  )
                }
              </div>
            </form>
          )
        )
      }
    </PsLayout >
  )
}

export default Recap